import {useEffect} from "react";
import {useAuthStore} from "../store/authStore";
import {useAppStore} from "../store/appStore";
import {auth} from "../firebaseConfig";
import {addBreadcrumb} from "../utils/auth";

// The file `consumer-frontend/src/hooks/useTokenReady.ts` contains a custom React hook named `useTokenReady`.
// This hook is responsible for managing the readiness state of an authentication token.
//
// This hook ensures that the authentication token is valid and ready to use,
// refreshing it once it is close to expiration.

const SAFETY_BUFFER_MS = 5 * 60 * 1000;

export const useTokenReady = () => {
    const idToken = useAuthStore(state => state.idToken);
    const isIntegration = useAppStore((state) => state.isIntegration);

    useEffect(() => {
        if (!idToken) return;

        if (isIntegration) {
            console.info('[Token Ready] In integration mode. Disable token ready check');
            addBreadcrumb('[Token Ready] In integration mode. Disable token ready check')

            useAuthStore.setState({tokenReady: true});
            return;
        }

        const user = auth.currentUser
        if (!user) return;

        const checkReadiness = async () => {
            const tokenResult = await user.getIdTokenResult();
            const expirationTime = new Date(tokenResult.expirationTime).getTime();
            const timeUntilExpiration = expirationTime - Date.now();
            const timeUntilRefresh = timeUntilExpiration - SAFETY_BUFFER_MS;
            console.info('[Token Ready] check readiness. Time until refresh (min): ', timeUntilRefresh / 60000)

            if (timeUntilRefresh <= 0) {
                await user.getIdToken(true);
                console.info("[Token Ready] Token has been refreshed");
                addBreadcrumb('[Token Ready] Token has been refreshed')
            } else{
                addBreadcrumb('[Token Ready] Token is ready', {timeUntilRefresh})
            }

            console.info('[Token Ready] Token is ready')

            useAuthStore.setState({tokenReady: true});
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;
            addBreadcrumb('[Token Ready] Visibility changed. Scheduling checkReadiness...')

            useAuthStore.setState({tokenReady: false});
            checkReadiness()
        };

        checkReadiness()

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [idToken, isIntegration]);
};
